import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  makeStyles,
  Button,
  Tooltip,
  Typography,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  Paper,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import FilterListIcon from '@material-ui/icons/FilterList';
import SearchIcon from '@material-ui/icons/Search';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { Octokit } from '@octokit/rest';
import { homepageReposApiRef } from '../../api';
import { HomepageRepo } from '../../types';
import { githubClientCache } from '../../utils/githubCache';
import { CollapsibleSection } from '../CollapsibleSection';
import { GitRepoContributionDialog } from '../GitRepoContributionDialog';
import { GitRepoListItem } from '../GitRepoListItem';

const useStyles = makeStyles(theme => ({
  addButton: {
    marginLeft: theme.spacing(1),
  },
  viewAllLink: {
    display: 'block',
    textAlign: 'center',
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  repoList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  orgSelect: {
    minWidth: 200,
  },
  languageSelect: {
    minWidth: 150,
  },
  searchField: {
    minWidth: 200,
    '& .MuiInputBase-root': {
      height: 40,
    },
  },
  toggleLabel: {
    marginLeft: theme.spacing(1),
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
    },
  },
  hiddenChip: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },
  hiddenPopover: {
    padding: theme.spacing(2),
    minWidth: 300,
    maxWidth: 400,
  },
  hiddenPopoverTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  hiddenList: {
    maxHeight: 300,
    overflow: 'auto',
  },
  hiddenListItem: {
    paddingRight: theme.spacing(6),
  },
  unhideButton: {
    color: theme.palette.primary.main,
  },
}));

export interface GitRepoSectionProps {
  defaultCollapsed?: boolean;
  editMode?: boolean;
  onHide?: () => void;
  maxRepos?: number;
  /** Filter repos to only show those associated with selected group refs */
  selectedGroupRefs?: string[];
  /** Whether the user chip is selected (show user-specific repos) */
  userSelected?: boolean;
  /** When true, "View all" expands inline instead of navigating to /github-repositories */
  expandable?: boolean;
  /** Start with all repos shown (only works when expandable is true) */
  initialExpanded?: boolean;
  /** Hide the section header/wrapper (for embedding in other pages) */
  hideHeader?: boolean;
}

const REPO_LIMIT = 10;

export const GitRepoSection = ({
  defaultCollapsed = false,
  editMode = false,
  onHide,
  maxRepos = REPO_LIMIT,
  selectedGroupRefs,
  userSelected = true,
  expandable = false,
  initialExpanded = false,
  hideHeader = false,
}: GitRepoSectionProps) => {
  const classes = useStyles();
  const reposApi = useApi(homepageReposApiRef);
  const githubAuthApi = useApi(githubAuthApiRef);

  const [savedRepos, setSavedRepos] = useState<HomepageRepo[]>([]);
  const [activityRepos, setActivityRepos] = useState<HomepageRepo[]>([]);
  const [_userRepos, setUserRepos] = useState<HomepageRepo[]>([]); // User's direct permission repos
  const [jwtGroups, setJwtGroups] = useState<string[]>([]);
  const [userRef, setUserRef] = useState<string | undefined>();

  // Merge JWT-based groups with GitHub team refs from the teams bar so components
  // are aware of the user's groups even when the JWT ent claim is empty.
  const userGroups = useMemo(() => {
    const merged = new Set([...jwtGroups, ...(selectedGroupRefs || [])]);
    return Array.from(merged);
  }, [jwtGroups, selectedGroupRefs]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [droppedUrl, setDroppedUrl] = useState<string | undefined>();
  const [openedFromDrop, setOpenedFromDrop] = useState(false);
  const [octokit, setOctokit] = useState<Octokit | null>(null);
  const [_githubUsername, setGithubUsername] = useState<string>('');
  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [hiddenRepos, setHiddenRepos] = useState<Set<string>>(new Set());
  const [editingRepo, setEditingRepo] = useState<HomepageRepo | null>(null);
  const [hiddenPopoverAnchor, setHiddenPopoverAnchor] = useState<HTMLElement | null>(null);
  const initialOrgFilterSet = useRef(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [committedReposOnly, setCommittedReposOnly] = useState(false);
  const [languageFilter, setLanguageFilter] = useState<string>('all');
  const [userContributedRepos, setUserContributedRepos] = useState<Set<string>>(new Set());
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  // Initialize GitHub client
  const initializeGitHub = useCallback(async () => {
    try {
      const accessToken = await githubAuthApi.getAccessToken(['repo', 'read:org', 'read:user']);
      const client = new Octokit({ auth: accessToken });
      setOctokit(client);

      const { data: user } = await client.users.getAuthenticated();
      setGithubUsername(user.login);

      return { client, username: user.login };
    } catch (err) {
      console.error('Failed to initialize GitHub:', err);
      return null;
    }
  }, [githubAuthApi]);

  // Fetch repos from teams with write, admin, or maintain permissions
  const fetchTeamRepos = useCallback(async (client: Octokit, user: string) => {
    try {
      // First, fetch all teams the user belongs to
      const { data: teams } = await client.request('GET /user/teams', {
        per_page: 100,
      });

      const repoMap = new Map<string, HomepageRepo>();

      // For each team, fetch repositories with appropriate permissions
      await Promise.all(
        teams.map(async (team: { slug: string; organization: { login: string } }) => {
          try {
            const { data: repos } = await client.request(
              'GET /orgs/{org}/teams/{team_slug}/repos',
              {
                org: team.organization.login,
                team_slug: team.slug,
                per_page: 100,
              },
            );

            for (const repo of repos) {
              // Only include repos with write, admin, or maintain permissions
              const permissions = repo.permissions as {
                push?: boolean;
                admin?: boolean;
                maintain?: boolean;
              } | undefined;

              if (permissions?.push || permissions?.admin || permissions?.maintain) {
                const key = repo.full_name.toLowerCase();
                const groupRef = `group:default/${team.slug}`;

                if (repoMap.has(key)) {
                  // Add team group to existing repo
                  const existing = repoMap.get(key)!;
                  if (!existing.groups.includes(groupRef)) {
                    existing.groups.push(groupRef);
                  }
                } else {
                  repoMap.set(key, {
                    id: `team-${repo.id}`,
                    repoUrl: repo.html_url,
                    owner: repo.owner.login,
                    name: repo.name,
                    fullName: repo.full_name,
                    groups: [groupRef],
                    createdBy: user,
                    createdAt: new Date().toISOString(),
                    language: repo.language || undefined,
                    lastActivity: repo.pushed_at || undefined,
                    isFromActivity: true,
                    stargazersCount: repo.stargazers_count,
                    description: repo.description || undefined,
                    lastCommitBranch: repo.default_branch,
                  });
                }
              }
            }
          } catch (err) {
            console.error(`Failed to fetch repos for team ${team.slug}:`, err);
          }
        }),
      );

      return Array.from(repoMap.values());
    } catch (err) {
      console.error('Failed to fetch team repos:', err);
      return [];
    }
  }, []);

  // Fetch repos user committed to in the last month
  const fetchActivityRepos = useCallback(async (client: Octokit, user: string) => {
    try {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      const dateStr = oneMonthAgo.toISOString().split('T')[0];

      // Search for commits by this user in the last month
      const { data } = await client.search.commits({
        q: `author:${user} committer-date:>${dateStr}`,
        per_page: 100,
        sort: 'committer-date',
        order: 'desc',
      });

      // Extract unique repositories
      const repoMap = new Map<string, HomepageRepo>();
      for (const item of data.items) {
        const fullName = item.repository.full_name;
        if (!repoMap.has(fullName)) {
          repoMap.set(fullName, {
            id: `activity-${item.repository.id}`,
            repoUrl: item.repository.html_url,
            owner: item.repository.owner.login,
            name: item.repository.name,
            fullName: fullName,
            groups: [],
            createdBy: user,
            createdAt: new Date().toISOString(),
            language: undefined,
            lastActivity: item.commit.committer?.date,
            isFromActivity: true,
            isUserContributor: true, // User has committed to this repo
            stargazersCount: undefined,
            description: item.repository.description || undefined,
            lastCommitAuthor: item.author?.login || item.commit.author?.name,
          });
        }
      }

      // Fetch additional repo metadata for up to 20 repos
      const repos = Array.from(repoMap.values()).slice(0, 20);
      const enhancedRepos = await Promise.all(
        repos.map(async repo => {
          try {
            const [repoDataResult, latestCommitResult] = await Promise.all([
              client.repos.get({
                owner: repo.owner,
                repo: repo.name,
              }),
              client.repos.listCommits({
                owner: repo.owner,
                repo: repo.name,
                per_page: 1,
              }).catch(() => ({ data: [] })),
            ]);

            const repoData = repoDataResult.data;
            const latestCommit = latestCommitResult.data[0];

            return {
              ...repo,
              language: repoData.language || undefined,
              stargazersCount: repoData.stargazers_count,
              lastActivity: repoData.pushed_at || repo.lastActivity,
              lastCommitBranch: repoData.default_branch,
              lastCommitAuthor: latestCommit?.author?.login || latestCommit?.commit?.author?.name || repo.lastCommitAuthor,
            };
          } catch {
            return repo;
          }
        }),
      );

      return enhancedRepos;
    } catch (err) {
      console.error('Failed to fetch activity repos:', err);
      return [];
    }
  }, []);

  // Fetch repos user has direct permissions on (owner, collaborator)
  const fetchUserRepos = useCallback(async (client: Octokit, user: string) => {
    try {
      // Fetch repos where user is owner or collaborator
      const { data: repos } = await client.repos.listForAuthenticatedUser({
        affiliation: 'owner,collaborator',
        per_page: 100,
        sort: 'pushed',
        direction: 'desc',
      });

      return repos
        .filter(repo => {
          // Only include repos with write/admin permissions
          const permissions = repo.permissions as {
            push?: boolean;
            admin?: boolean;
            maintain?: boolean;
          } | undefined;
          return permissions?.push || permissions?.admin || permissions?.maintain;
        })
        .map(repo => ({
          id: `user-${repo.id}`,
          repoUrl: repo.html_url,
          owner: repo.owner?.login || '',
          name: repo.name,
          fullName: repo.full_name,
          groups: [], // No team association - user-specific
          createdBy: user,
          createdAt: new Date().toISOString(),
          language: repo.language || undefined,
          lastActivity: repo.pushed_at || undefined,
          isFromActivity: true,
          isUserRepo: true, // Mark as user-specific repo
          stargazersCount: repo.stargazers_count,
          description: repo.description || undefined,
          lastCommitBranch: repo.default_branch,
        } as HomepageRepo));
    } catch (err) {
      console.error('Failed to fetch user repos:', err);
      return [];
    }
  }, []);

  // Enrich repos with last commit author using cache (5-minute TTL for static data)
  const enrichReposWithLastCommit = useCallback(async (
    client: Octokit,
    repos: HomepageRepo[],
  ): Promise<HomepageRepo[]> => {
    // Only enrich repos that don't already have lastCommitAuthor AND have activity (not empty repos)
    // Skip repos without lastActivity as they're likely empty (409 error from GitHub)
    const reposToEnrich = repos.filter(r => !r.lastCommitAuthor && r.lastActivity);
    if (reposToEnrich.length === 0) return repos;

    // Process in batches of 5 to avoid rate limits
    const batchSize = 5;
    const enrichedMap = new Map<string, { author?: string; branch?: string }>();

    for (let i = 0; i < reposToEnrich.length; i += batchSize) {
      const batch = reposToEnrich.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (repo) => {
          const key = repo.fullName.toLowerCase();

          // Check cache first (5-minute TTL)
          const cached = githubClientCache.getLastCommitInfo(repo.owner, repo.name);
          if (cached) {
            enrichedMap.set(key, {
              author: cached.authorLogin || cached.authorName,
              branch: cached.branch,
            });
            return;
          }

          // Fetch from API
          try {
            const { data: commits } = await client.repos.listCommits({
              owner: repo.owner,
              repo: repo.name,
              per_page: 1,
            });

            if (commits.length > 0) {
              const commit = commits[0];
              const info = {
                branch: repo.lastCommitBranch || '',
                authorLogin: commit.author?.login,
                authorName: commit.commit?.author?.name,
                date: commit.commit?.author?.date,
              };

              // Cache for 5 minutes
              githubClientCache.setLastCommitInfo(repo.owner, repo.name, info);

              enrichedMap.set(key, {
                author: info.authorLogin || info.authorName,
                branch: info.branch,
              });
            }
          } catch {
            // Silently fail for individual repos
          }
        }),
      );
    }

    // Merge enriched data back into repos
    return repos.map(repo => {
      const enriched = enrichedMap.get(repo.fullName.toLowerCase());
      if (enriched) {
        return {
          ...repo,
          lastCommitAuthor: enriched.author || repo.lastCommitAuthor,
          lastCommitBranch: enriched.branch || repo.lastCommitBranch,
        };
      }
      return repo;
    });
  }, []);

  // Fetch repos user has contributed to using GraphQL (default: last year)
  const fetchUserContributions = useCallback(async (client: Octokit, username: string): Promise<Set<string>> => {
    try {
      const query = `
        query($username: String!) {
          user(login: $username) {
            contributionsCollection {
              commitContributionsByRepository(maxRepositories: 100) {
                repository {
                  nameWithOwner
                }
              }
              issueContributionsByRepository(maxRepositories: 100) {
                repository {
                  nameWithOwner
                }
              }
              pullRequestContributionsByRepository(maxRepositories: 100) {
                repository {
                  nameWithOwner
                }
              }
              pullRequestReviewContributionsByRepository(maxRepositories: 100) {
                repository {
                  nameWithOwner
                }
              }
            }
          }
        }
      `;

      const response: any = await client.graphql(query, { username });

      const contributedRepos = new Set<string>();
      const collection = response.user?.contributionsCollection;

      // Add repos from all contribution types
      const contributionTypes = [
        'commitContributionsByRepository',
        'issueContributionsByRepository',
        'pullRequestContributionsByRepository',
        'pullRequestReviewContributionsByRepository',
      ];

      for (const type of contributionTypes) {
        const contribs = collection?.[type] || [];
        for (const contrib of contribs) {
          if (contrib.repository?.nameWithOwner) {
            contributedRepos.add(contrib.repository.nameWithOwner.toLowerCase());
          }
        }
      }

      return contributedRepos;
    } catch (err) {
      console.error('Failed to fetch user contributions via GraphQL:', err);
      return new Set();
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      // Load saved repos and user info
      const [savedData, userInfo] = await Promise.all([
        reposApi.getRepos(selectedGroupRefs),
        reposApi.getUserInfo(),
      ]);
      setSavedRepos(savedData);
      setJwtGroups(userInfo.groups);
      setUserRef(userInfo.userRef);

      // Initialize GitHub and fetch activity repos + team repos + user repos
      const github = await initializeGitHub();
      if (github) {
        // Fetch commit activity repos, team permission repos, user repos, and 6-month contributions in parallel
        const [commitRepos, teamReposRaw, directUserReposRaw, contributedRepos] = await Promise.all([
          fetchActivityRepos(github.client, github.username),
          fetchTeamRepos(github.client, github.username),
          fetchUserRepos(github.client, github.username),
          fetchUserContributions(github.client, github.username),
        ]);

        // Store the contributed repos for filtering
        setUserContributedRepos(contributedRepos);

        // Enrich team and user repos with last commit author (uses 5-min cache)
        const [teamRepos, directUserRepos] = await Promise.all([
          enrichReposWithLastCommit(github.client, teamReposRaw),
          enrichReposWithLastCommit(github.client, directUserReposRaw),
        ]);

        // Store user repos separately for filtering
        setUserRepos(directUserRepos);

        // Merge team repos, user repos, and activity repos
        const mergedMap = new Map<string, HomepageRepo>();

        // Add team repos first (they have group associations)
        for (const repo of teamRepos) {
          mergedMap.set(repo.fullName.toLowerCase(), repo);
        }

        // Add user repos (mark as user-specific)
        for (const repo of directUserRepos) {
          const key = repo.fullName.toLowerCase();
          if (mergedMap.has(key)) {
            // Merge: keep existing data, mark as user repo too
            const existing = mergedMap.get(key)!;
            mergedMap.set(key, {
              ...existing,
              isUserRepo: true,
              lastActivity: repo.lastActivity || existing.lastActivity,
              lastCommitBranch: repo.lastCommitBranch || existing.lastCommitBranch,
            });
          } else {
            mergedMap.set(key, { ...repo, isUserRepo: true });
          }
        }

        // Add commit activity repos (merge with existing if present)
        for (const repo of commitRepos) {
          const key = repo.fullName.toLowerCase();
          if (mergedMap.has(key)) {
            // Merge: keep team groups, update metadata from commit activity
            const existing = mergedMap.get(key)!;
            mergedMap.set(key, {
              ...existing,
              lastActivity: repo.lastActivity || existing.lastActivity,
              lastCommitBranch: repo.lastCommitBranch || existing.lastCommitBranch,
              lastCommitAuthor: repo.lastCommitAuthor || existing.lastCommitAuthor,
              isUserContributor: true, // User has committed to this repo
            });
          } else {
            mergedMap.set(key, repo);
          }
        }

        setActivityRepos(Array.from(mergedMap.values()));
      }
    } catch (err) {
      console.error('Failed to load git repos:', err);
    } finally {
      setLoading(false);
    }
  }, [reposApi, selectedGroupRefs, initializeGitHub, fetchActivityRepos, fetchTeamRepos, fetchUserRepos, enrichReposWithLastCommit, fetchUserContributions]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Extract unique orgs from activity repos for filtering
  const availableOrgs = useMemo(() => {
    const orgs = new Set<string>();
    for (const repo of activityRepos) {
      orgs.add(repo.owner);
    }
    return Array.from(orgs).sort();
  }, [activityRepos]);

  // Extract unique languages from all repos for filtering
  const availableLanguages = useMemo(() => {
    const languages = new Set<string>();
    for (const repo of [...savedRepos, ...activityRepos]) {
      if (repo.language) {
        languages.add(repo.language);
      }
    }
    return Array.from(languages).sort();
  }, [savedRepos, activityRepos]);

  // Set default org filter to the most common org when activity loads (only once)
  useEffect(() => {
    if (availableOrgs.length > 0 && !initialOrgFilterSet.current) {
      // Find the most common org in activity repos
      const orgCounts = new Map<string, number>();
      for (const repo of activityRepos) {
        orgCounts.set(repo.owner, (orgCounts.get(repo.owner) || 0) + 1);
      }
      let maxCount = 0;
      let mostCommonOrg = availableOrgs[0];
      for (const [org, count] of orgCounts) {
        if (count > maxCount) {
          maxCount = count;
          mostCommonOrg = org;
        }
      }
      setOrgFilter(mostCommonOrg);
      initialOrgFilterSet.current = true;
    }
  }, [availableOrgs, activityRepos]);

  // Merge and deduplicate repos
  const mergedRepos = useMemo(() => {
    const repoMap = new Map<string, HomepageRepo>();

    // If no groups selected and user not selected, show nothing
    const noGroupsSelected = !selectedGroupRefs || selectedGroupRefs.length === 0;
    if (noGroupsSelected && !userSelected) {
      return [];
    }

    // STEP 1: Add ALL saved repos first (no filtering yet - we need to merge groups first)
    for (const repo of savedRepos) {
      const isUserSpecificSaved = userRef && repo.groups.some(g => g === userRef);
      repoMap.set(repo.fullName.toLowerCase(), {
        ...repo,
        isFromActivity: false,
        isUserRepo: !!isUserSpecificSaved,
      });
    }

    // STEP 2: Merge ALL activity repos (merge groups with saved repos)
    for (const repo of activityRepos) {
      // Apply org filter only to activity repos
      if (orgFilter !== 'all' && repo.owner !== orgFilter) {
        continue;
      }

      const key = repo.fullName.toLowerCase();
      if (!repoMap.has(key)) {
        // New repo from activity
        repoMap.set(key, repo);
      } else {
        // Merge metadata AND groups from activity into saved repo
        const existing = repoMap.get(key)!;
        // Combine groups from both sources (union of groups)
        const mergedGroups = [...new Set([...existing.groups, ...repo.groups])];
        repoMap.set(key, {
          ...existing,
          groups: mergedGroups,
          language: existing.language || repo.language,
          lastActivity: repo.lastActivity || existing.lastActivity,
          stargazersCount: repo.stargazersCount ?? existing.stargazersCount,
          isUserRepo: existing.isUserRepo || repo.isUserRepo,
        });
      }
    }

    // STEP 3: Now filter based on selection (after groups have been merged)
    // Uses UNION logic: show if ANY selected team matches OR if user repo and user selected
    const shouldShowRepo = (groups: string[], isUserRepo: boolean): boolean => {
      const isUserSpecific = userRef && groups.some(g => g === userRef);
      const matchesUser = (isUserSpecific || isUserRepo) && userSelected;
      const matchesTeam = !noGroupsSelected && groups.some(g => selectedGroupRefs!.includes(g));
      return matchesUser || matchesTeam;
    };

    // Filter out hidden repos, apply search filter, selection filter, and sort by last activity
    return Array.from(repoMap.values())
      .filter(repo => shouldShowRepo(repo.groups, !!repo.isUserRepo))
      .filter(repo => !hiddenRepos.has(repo.fullName.toLowerCase()))
      .filter(repo => {
        // Apply search filter
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          return repo.fullName.toLowerCase().includes(query) ||
                 repo.name.toLowerCase().includes(query) ||
                 repo.owner.toLowerCase().includes(query);
        }
        return true;
      })
      .filter(repo => {
        // Apply language filter
        if (languageFilter !== 'all') {
          if (repo.language !== languageFilter) {
            return false;
          }
        }
        return true;
      })
      .filter(repo => {
        // Apply committed repos only filter using GraphQL contributionsCollection
        // Includes commits, issues, PRs, and PR reviews from the last year
        if (committedReposOnly) {
          return userContributedRepos.has(repo.fullName.toLowerCase());
        }
        return true;
      })
      .sort((a, b) => {
        const aDate = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
        const bDate = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
        return bDate - aDate;
      });
  }, [savedRepos, activityRepos, orgFilter, hiddenRepos, selectedGroupRefs, searchQuery, committedReposOnly, userSelected, userRef, languageFilter, userContributedRepos]);

  // When expandable mode is active and expanded, show all repos
  const effectiveMaxRepos = (expandable && isExpanded) ? mergedRepos.length : maxRepos;
  const displayedRepos = mergedRepos.slice(0, effectiveMaxRepos);
  const hasMoreRepos = mergedRepos.length > maxRepos;

  const handleRepoDrop = useCallback((url: string) => {
    // Check if it's a GitHub URL
    if (url.includes('github.com')) {
      setDroppedUrl(url);
      setOpenedFromDrop(true);
      setDialogOpen(true);
    }
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setDroppedUrl(undefined);
    setOpenedFromDrop(false);
    setEditingRepo(null);
  }, []);

  const handleAddRepo = useCallback(async (input: any) => {
    await reposApi.createRepo(input);
    await loadData();
  }, [reposApi, loadData]);

  const handleHideSection = useCallback(async () => {
    onHide?.();
  }, [onHide]);

  const handleHideRepo = useCallback((repo: HomepageRepo) => {
    setHiddenRepos(prev => new Set(prev).add(repo.fullName.toLowerCase()));
  }, []);

  const handleEditRepoGroups = useCallback((repo: HomepageRepo) => {
    // For activity repos, open dialog to save to list
    // For saved repos, open dialog to edit groups
    setEditingRepo(repo);
    setDroppedUrl(repo.repoUrl);
    setOpenedFromDrop(false);
    setDialogOpen(true);
  }, []);

  const handleOrgFilterChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
    setOrgFilter(event.target.value as string);
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleCommittedToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCommittedReposOnly(event.target.checked);
  }, []);

  const handleLanguageFilterChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
    setLanguageFilter(event.target.value as string);
  }, []);

  const handleHiddenChipClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setHiddenPopoverAnchor(event.currentTarget);
  }, []);

  const handleHiddenPopoverClose = useCallback(() => {
    setHiddenPopoverAnchor(null);
  }, []);

  const handleUnhideRepo = useCallback((repoKey: string) => {
    setHiddenRepos(prev => {
      const next = new Set(prev);
      next.delete(repoKey);
      return next;
    });
  }, []);

  const handleDeleteRepo = useCallback(async (repo: HomepageRepo) => {
    await reposApi.deleteRepo(repo.id);
    await loadData();
  }, [reposApi, loadData]);

  // Get list of hidden repo objects for display
  const hiddenReposList = useMemo(() => {
    const allRepos = [...savedRepos, ...activityRepos];
    const repoMap = new Map<string, HomepageRepo>();
    for (const repo of allRepos) {
      repoMap.set(repo.fullName.toLowerCase(), repo);
    }
    return Array.from(hiddenRepos)
      .map(key => repoMap.get(key))
      .filter((repo): repo is HomepageRepo => repo !== undefined);
  }, [savedRepos, activityRepos, hiddenRepos]);

  // Render the inner content (filters + repo list)
  const renderContent = () => (
    <>
      {/* Filter controls - always visible */}
      <Box className={classes.filterContainer}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search repositories..."
          value={searchQuery}
          onChange={handleSearchChange}
          className={classes.searchField}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8, color: 'rgba(0,0,0,0.54)' }} />,
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={committedReposOnly}
              onChange={handleCommittedToggle}
              color="primary"
              size="small"
            />
          }
          label="committed repos only"
          className={classes.toggleLabel}
        />
        {availableOrgs.length > 0 && (
          <>
            <Typography className={classes.filterLabel}>
              <FilterListIcon fontSize="small" />
            </Typography>
            <FormControl variant="outlined" size="small" className={classes.orgSelect}>
              <InputLabel id="org-filter-label">Organization</InputLabel>
              <Select
                labelId="org-filter-label"
                value={orgFilter}
                onChange={handleOrgFilterChange}
                label="Organization"
              >
                <MenuItem value="all">All organizations</MenuItem>
                {availableOrgs.map(org => (
                  <MenuItem key={org} value={org}>{org}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {availableLanguages.length > 0 && (
          <FormControl variant="outlined" size="small" className={classes.languageSelect}>
            <InputLabel id="language-filter-label">Language</InputLabel>
            <Select
              labelId="language-filter-label"
              value={languageFilter}
              onChange={handleLanguageFilterChange}
              label="Language"
            >
              <MenuItem value="all">All languages</MenuItem>
              {availableLanguages.map(lang => (
                <MenuItem key={lang} value={lang}>{lang}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {editMode && hiddenRepos.size > 0 && (
          <Chip
            icon={<VisibilityOffIcon fontSize="small" />}
            label={`${hiddenRepos.size} hidden`}
            size="small"
            onClick={handleHiddenChipClick}
            className={classes.hiddenChip}
            variant="outlined"
          />
        )}
      </Box>

      {displayedRepos.length === 0 ? (
        <Typography variant="body2" color="textSecondary" style={{ padding: 16, textAlign: 'center' }}>
          No Matching Repositories Found
        </Typography>
      ) : (
        <div className={classes.repoList}>
          {displayedRepos.map(repo => (
            <GitRepoListItem
              key={repo.id}
              repo={repo}
              octokit={octokit}
              editMode={editMode}
              onHide={handleHideRepo}
              onEditGroups={handleEditRepoGroups}
              onDelete={handleDeleteRepo}
              userGroups={userGroups}
            />
          ))}
          {hasMoreRepos && !isExpanded && (
            expandable ? (
              <Button
                onClick={() => setIsExpanded(true)}
                className={classes.viewAllLink}
                color="primary"
              >
                <Typography variant="body2" color="primary">
                  View all {mergedRepos.length} repositories
                </Typography>
              </Button>
            ) : (
              <Link
                href="/github-repositories"
                className={classes.viewAllLink}
              >
                <Typography variant="body2" color="primary">
                  View all {mergedRepos.length} repositories
                </Typography>
              </Link>
            )
          )}
          {expandable && isExpanded && hasMoreRepos && (
            <Button
              onClick={() => setIsExpanded(false)}
              className={classes.viewAllLink}
              color="primary"
            >
              <Typography variant="body2" color="primary">
                Show fewer repositories
              </Typography>
            </Button>
          )}
        </div>
      )}
    </>
  );

  if (loading) {
    if (hideHeader) {
      return <Progress />;
    }
    return (
      <CollapsibleSection title="Git Repositories" sectionKey="git-repos" editMode={editMode}>
        <Progress />
      </CollapsibleSection>
    );
  }

  // When hideHeader is true, render content directly without CollapsibleSection wrapper
  if (hideHeader) {
    return (
      <>
        <Paper style={{ padding: 16, marginBottom: 16 }}>
          {renderContent()}
        </Paper>

        <GitRepoContributionDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSubmit={handleAddRepo}
          userGroups={userGroups}
          userRef={userRef}
          initialUrl={droppedUrl}
          preSelectMyself={openedFromDrop}
          initialGroups={editingRepo?.groups}
        />

        {/* Hidden repos popover */}
        <Popover
          open={Boolean(hiddenPopoverAnchor)}
          anchorEl={hiddenPopoverAnchor}
          onClose={handleHiddenPopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box className={classes.hiddenPopover}>
            <Typography className={classes.hiddenPopoverTitle}>
              <VisibilityOffIcon fontSize="small" />
              Hidden Repositories
            </Typography>
            {hiddenReposList.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No hidden repositories
              </Typography>
            ) : (
              <List dense className={classes.hiddenList}>
                {hiddenReposList.map(repo => (
                  <ListItem key={repo.fullName} className={classes.hiddenListItem}>
                    <ListItemText
                      primary={repo.fullName}
                      secondary={repo.isFromActivity ? 'Auto-detected' : 'Saved'}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Unhide repository">
                        <IconButton
                          size="small"
                          className={classes.unhideButton}
                          onClick={() => handleUnhideRepo(repo.fullName.toLowerCase())}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Popover>
      </>
    );
  }

  return (
    <>
      <CollapsibleSection
        title="Git Repositories"
        sectionKey="git-repos"
        defaultCollapsed={defaultCollapsed}
        onHide={handleHideSection}
        onLinkDrop={handleRepoDrop}
        editMode={editMode}
        action={
          <Tooltip title="Add repository">
            <Button
              size="small"
              color="primary"
              startIcon={<AddIcon />}
              onClick={e => {
                e.stopPropagation();
                setDialogOpen(true);
              }}
              className={classes.addButton}
            >
              Add
            </Button>
          </Tooltip>
        }
      >
        {renderContent()}
      </CollapsibleSection>

      <GitRepoContributionDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleAddRepo}
        userGroups={userGroups}
        userRef={userRef}
        initialUrl={droppedUrl}
        preSelectMyself={openedFromDrop}
        initialGroups={editingRepo?.groups}
      />

      {/* Hidden repos popover */}
      <Popover
        open={Boolean(hiddenPopoverAnchor)}
        anchorEl={hiddenPopoverAnchor}
        onClose={handleHiddenPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box className={classes.hiddenPopover}>
          <Typography className={classes.hiddenPopoverTitle}>
            <VisibilityOffIcon fontSize="small" />
            Hidden Repositories
          </Typography>
          {hiddenReposList.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No hidden repositories
            </Typography>
          ) : (
            <List dense className={classes.hiddenList}>
              {hiddenReposList.map(repo => (
                <ListItem key={repo.fullName} className={classes.hiddenListItem}>
                  <ListItemText
                    primary={repo.fullName}
                    secondary={repo.isFromActivity ? 'Auto-detected' : 'Saved'}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Unhide repository">
                      <IconButton
                        size="small"
                        className={classes.unhideButton}
                        onClick={() => handleUnhideRepo(repo.fullName.toLowerCase())}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};
